import { Clock } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TimePickerProps {
  value?: string;
  onChange: (time: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minuteStep?: 1 | 5 | 10 | 15 | 30;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function buildMinutes(step: number): number[] {
  const result: number[] = [];
  for (let m = 0; m < 60; m += step) {
    result.push(m);
  }
  return result;
}

function parseTime(value: string | undefined): { hour: string; minute: string } | null {
  if (!value) return null;
  const [h, m] = value.split(':');
  if (h === undefined || m === undefined) return null;
  return { hour: String(Number(h)), minute: String(Number(m)) };
}

function formatTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function TimePicker({
  value,
  onChange,
  placeholder = 'Время',
  disabled = false,
  className,
  minuteStep = 5,
}: TimePickerProps) {
  const parsed = parseTime(value);
  const minutes = buildMinutes(minuteStep);

  const handleHourChange = (hour: string) => {
    const currentMinute = parsed ? Number(parsed.minute) : 0;
    onChange(formatTime(Number(hour), currentMinute));
  };

  const handleMinuteChange = (minute: string) => {
    const currentHour = parsed ? Number(parsed.hour) : 0;
    onChange(formatTime(currentHour, Number(minute)));
  };

  const handleClear = () => {
    onChange('');
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-empty={!value}
          disabled={disabled}
          className={cn(
            'justify-start text-left font-normal data-[empty=true]:text-muted-foreground',
            className
          )}
        >
          <Clock className="mr-2 h-4 w-4" />
          {value ? value : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <Select
              value={parsed?.hour}
              onValueChange={handleHourChange}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Час" />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((hour) => (
                  <SelectItem key={hour} value={String(hour)}>
                    {String(hour).padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-muted-foreground text-lg font-medium">:</span>
            <Select
              value={parsed?.minute}
              onValueChange={handleMinuteChange}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Мин" />
              </SelectTrigger>
              <SelectContent>
                {minutes.map((minute) => (
                  <SelectItem key={minute} value={String(minute)}>
                    {String(minute).padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {value && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={handleClear}
            >
              Очистить
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
